import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import { defineMessages } from 'react-intl';

import {
    CalculatorStepPanel,
    Sizes,
    Question,
    useForm,
    FormComputed,
    FormValue,
} from '@ratehub/base-ui';
import {
    MORTGAGE_PLACEHOLDERS,
    AMORTIZATION_OPTIONS_EXTENDED,
    createAmortizationOptionResolver,
    calculatePurchaseMortgageTotal,
    DownPaymentInput,
} from '@ratehub/mtg-common';


function AmortizationCalcInputStep() {
    const form = useForm();
    const intl = useIntl();

    const isMortgage = form?.values?.loanType === LOAN_TYPE_VALUES.MORTGAGE;
    const isLoan = form?.values?.loanType === LOAN_TYPE_VALUES.LOAN;

    return (
        <Container
            stepNumber={1}
            isVisible
            ctaText={(
                <FormattedMessage
                    id="calculators.Amortization.CTAText"
                    defaultMessage="View my amortization schedule"
                />
            )}
            ctaDataName="AmortizationCalculator.InputStep-viewAmortization"
            hasCompleteButton={true}
        >
            <h2 className="header rh-text-l weight-regular">
                <FormattedMessage
                    id="calculators.Amortization.header"
                    defaultMessage="Calculate your amortization schedule"
                />
            </h2>

            <hr className="rh-horizontal-rule rh-mb-1_5 rh-width-100p" />

            <Question
                className="question-container"
                field={{
                    name: 'loanType',
                    type: 'choice',
                    options: LOAN_TYPE_OPTIONS,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Amortization.loanTypeLabel"
                        defaultMessage="Loan type"
                    />
                )}
                flowDirection="row"
                isSideBySide={true}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.Amortization.loanTypeQuicktip"
                        defaultMessage="Choose either a mortgage loan or a general loan (e.g. personal loan, student loan, etc.)."
                    />
                )}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.Amortization.loanTypeTooltip"
                        defaultMessage="A mortgage is a loan for the purpose of purchasing a property and is known
                                        as a secured loan. It requires a cash down payment up front and the lender
                                        can take possession of the property if you don't repay the loan on time.
                                        Other general loans (such as personal, student, or credit card loans) are typically
                                        unsecured and don't require you to provide any cash up front in order to borrow money."
                    />
                )}
            />

            <If condition={isMortgage} >
                <Question
                    className="question-container"
                    field={{
                        name: 'homePrice',
                        type: 'integer',
                        required: isMortgage,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.homePriceLabel"
                            defaultMessage="Home price"
                        />
                    )}
                    placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                    type="currency"
                    isSideBySide={true}
                    showOptionalText={false}
                />

                <FormValue>
                    {({ homePrice }) => (
                        <Question
                            field={{
                                name: 'downPayment',
                                required: isMortgage,
                            }}
                            type={DownPaymentInput}
                            purchasePrice={homePrice}
                            labelMessage={(
                                <FormattedMessage
                                    id="calculators.Amortization.downPaymentLabel"
                                    defaultMessage="Down payment"
                                />
                            )}
                            isSideBySide={true}
                            tooltipMessage={(
                                <FormattedMessage
                                    id="calculators.Amortization.downPaymentTooltip"
                                    defaultMessage="The amount of money you pay up front to obtain a mortgage. The minimum down payment
                                            in Canada is 5%. For down payments of less than 20%, homebuyers are required
                                            to purchase mortgage default insurance, commonly referred to as CMHC insurance."
                                />
                            )}
                            className="question-container"
                        />
                    )}
                </FormValue>

                <Question
                    className="question-container"
                    field={{
                        name: 'mortgageRate',
                        type: 'float',
                        required: isMortgage,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.mortgageRateLabel"
                            defaultMessage="Interest Rate"
                        />
                    )}
                    placeholder={MORTGAGE_PLACEHOLDERS.PERCENT_PLACEHOLDER}
                    type="percent"
                    isSideBySide={true}
                    showOptionalText={false}
                    tooltipMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.mortgageRateTooltip"
                            defaultMessage="The rate of interest you will pay on the outstanding balance of your mortgage."
                        />
                    )}
                />

                <Question
                    className="question-container"
                    field={{
                        name: 'amortizationPeriod',
                        type: 'choice',
                        value: 25,
                        options: AMORTIZATION_OPTIONS_EXTENDED,
                        validators: [
                            {
                                fn: (value) => form.values.downPaymentPercent < 20 ? value <= 25 : true,

                                message: (
                                    <FormattedMessage
                                        id="calculators.Amortization.amortizationPeriodErrormessage"
                                        defaultMessage="The maximum amortization period is 25 years for mortgages with less than 20% down."
                                    />
                                ),
                            } ],
                    }}
                    type="dropdown"
                    labelMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.amortizationPeriodLabel"
                            defaultMessage="Amortization period"
                        />
                    )}
                    isSideBySide={true}
                    tooltipMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.amortizationPeriodTooltip"
                            defaultMessage="The entire length of time you have to pay off your mortgage.
                                            In Canada, the maximum amortization period for insurable mortgages is 25 years.
                                            Longer amortization periods allow homeowners to make smaller monthly payments,
                                            but equate to more interest paid over the life of the mortgage."
                        />
                    )}
                    renderOptionLabel={createAmortizationOptionResolver(intl)}
                />

                <FormComputed
                    name="totalMortgage"
                    value={({ homePrice, downPayment }) => calculatePurchaseMortgageTotal(homePrice, downPayment?.dollars)}
                />
            </If>

            <If condition={isLoan}>
                <Question
                    className="question-container"
                    field={{
                        name: 'loanAmount',
                        type: 'integer',
                        required: isLoan,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.loanAmountLabel"
                            defaultMessage="Loan amount"
                        />
                    )}
                    placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                    type="currency"
                    isSideBySide={true}
                    tooltipMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.loanAmountTooltip"
                            defaultMessage="The total amount of money being borrowed."
                        />
                    )}
                    showOptionalText={false}
                />

                <Question
                    className="question-container"
                    field={{
                        name: 'loanRate',
                        type: 'float',
                        required: isLoan,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.loanRateLabel"
                            defaultMessage="Interest Rate"
                        />
                    )}
                    placeholder={MORTGAGE_PLACEHOLDERS.PERCENT_PLACEHOLDER}
                    type="percent"
                    isSideBySide={true}
                    tooltipMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.loanRateTooltip"
                            defaultMessage="The rate of interest you will pay on your loan."
                        />
                    )}
                    showOptionalText={false}
                />

                <Question
                    className="question-container"
                    field={{
                        name: 'loanPeriod',
                        type: 'choice',
                        value: 25,
                        options: AMORTIZATION_OPTIONS_EXTENDED,
                    }}
                    type="dropdown"
                    labelMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.loanPeriodLabel"
                            defaultMessage="Loan period"
                        />
                    )}
                    isSideBySide={true}
                    tooltipMessage={(
                        <FormattedMessage
                            id="calculators.Amortization.loanPeriodTooltip"
                            defaultMessage="The entire length of time you have to pay off your loan."
                        />
                    )}
                    renderOptionLabel={createAmortizationOptionResolver(intl)}
                />
            </If>

        </Container>
    );
}

const MESSAGES = defineMessages({
    MORTGAGE: {
        id: 'calculators.Amortization.mortgageChoice',
        defaultMessage: 'Mortgage',
    },
    LOAN: {
        id: 'calculators.Amortization.loanChoice',
        defaultMessage: 'Loan',
    },
});

const LOAN_TYPE_VALUES = {
    LOAN: 'Loan',
    MORTGAGE: 'Mortgage',
};

const LOAN_TYPE_OPTIONS = [
    { value: LOAN_TYPE_VALUES.MORTGAGE, label: MESSAGES.MORTGAGE },
    { value: LOAN_TYPE_VALUES.LOAN, label: MESSAGES.LOAN },
];

const Container = styled(CalculatorStepPanel)`
    padding: ${Sizes.SPACING.ONE_AND_A_HALF} ${Sizes.SPACING.ONE_AND_A_HALF} 0;

    > .header {
        margin-top: 0;
        margin-bottom: ${Sizes.SPACING.ONE_AND_A_HALF};
    }

    .question-container {
        margin-top: 0;
        margin-bottom: ${Sizes.SPACING.TWO};
    }

    .ctaButton {
        margin-top: ${Sizes.SPACING.ONE};
    }
`;

export default observer(AmortizationCalcInputStep);
